import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

function BusinessStrategy() {
  const [companyLanguage, setCompanyLanguage] = useState('en');

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    
    const handleStorageChange = () => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      setCompanyLanguage(updatedLang);
    };
    
    window.addEventListener('companyDataUpdated', handleStorageChange);
    
    const interval = setInterval(() => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      if (updatedLang !== companyLanguage) {
        setCompanyLanguage(updatedLang);
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  const isUrdu = companyLanguage === 'ur';

  return (
    <>
      <Helmet>
        <title>{isUrdu ? 'کاروباری مشاورت - Galla Mandi Bills & Khata' : 'Business Strategy - Galla Mandi Bills & Khata'}</title>
      </Helmet>
      
      <div
        className={`${isUrdu ? 'font-urdu' : ''} bg-brand-background min-h-screen`}
        style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
        dir={isUrdu ? 'rtl' : 'ltr'}
      >
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-brand-header via-green-700 to-brand-header text-white py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-72 h-72 bg-green-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
            <div className="absolute top-0 right-0 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          </div>
          <div className={`container mx-auto px-4 text-center relative z-10 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fade-in-up">
              {isUrdu ? 'کاروباری مشاورت' : 'Business Strategy'}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 font-medium">
              {isUrdu 
                ? 'اپنے کاروبار کو کامیابی کی طرف لے جانے کے لیے حکمت عملی' 
                : 'Strategic planning to take your business to success'}
            </p>
          </div>
        </section>

        {/* Strategy Section */}
        <section className={`py-20 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-brand-header mb-4">
                {isUrdu ? 'ہماری حکمت عملی' : 'Our Strategies'}
              </h2>
              <p className="text-gray-600 text-lg">
                {isUrdu ? 'کاروباری کامیابی کے لیے مکمل حل' : 'Complete solutions for business success'}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: "📊",
                  title: isUrdu ? "منصوبہ بندی" : "Planning",
                  titleUrdu: "منصوبہ بندی",
                  desc: isUrdu ? "مؤثر کاروباری منصوبہ بندی" : "Effective business planning",
                  descUrdu: "مؤثر کاروباری منصوبہ بندی"
                },
                {
                  icon: "🎯",
                  title: isUrdu ? "ہدف طے کرنا" : "Goal Setting",
                  titleUrdu: "ہدف طے کرنا",
                  desc: isUrdu ? "واضح اور قابل حصول اہداف" : "Clear and achievable goals",
                  descUrdu: "واضح اور قابل حصول اہداف"
                },
                {
                  icon: "📈",
                  title: isUrdu ? "ترقی" : "Growth",
                  titleUrdu: "ترقی",
                  desc: isUrdu ? "کاروبار کی پائیدار ترقی" : "Sustainable business growth",
                  descUrdu: "کاروبار کی پائیدار ترقی"
                },
                {
                  icon: "💡",
                  title: isUrdu ? "جدت" : "Innovation",
                  titleUrdu: "جدت",
                  desc: isUrdu ? "جدید خیالات اور حل" : "Innovative ideas and solutions",
                  descUrdu: "جدید خیالات اور حل"
                },
                {
                  icon: "🤝",
                  title: isUrdu ? "شراکت داری" : "Partnership",
                  titleUrdu: "شراکت داری",
                  desc: isUrdu ? "مضبوط کاروباری شراکت داری" : "Strong business partnerships",
                  descUrdu: "مضبوط کاروباری شراکت داری"
                },
                {
                  icon: "⚡",
                  title: isUrdu ? "کارکردگی" : "Efficiency",
                  titleUrdu: "کارکردگی",
                  desc: isUrdu ? "بہتر کارکردگی اور پیداواری صلاحیت" : "Improved efficiency and productivity",
                  descUrdu: "بہتر کارکردگی اور پیداواری صلاحیت"
                }
              ].map((strategy, idx) => (
                <div
                  key={idx}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
                >
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {strategy.icon}
                  </div>
                  <h3 className={`text-2xl font-bold text-gray-900 mb-3 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? strategy.titleUrdu : strategy.title}
                  </h3>
                  <p className={`text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? strategy.descUrdu : strategy.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-brand-header to-green-700 text-white">
          <div className={`container mx-auto px-4 text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h2 className="text-4xl font-bold mb-4">
              {isUrdu ? 'اپنے کاروبار کو بڑھائیں' : 'Grow Your Business'}
            </h2>
            <p className="text-xl mb-8 text-white/90">
              {isUrdu ? 'ہماری حکمت عملی کے ساتھ کامیابی حاصل کریں' : 'Achieve success with our strategies'}
            </p>
            <Link
              to="/contact"
              className="bg-brand-button hover:bg-yellow-300 text-gray-900 font-bold px-8 py-4 rounded-xl transition transform hover:scale-105 hover:shadow-lg inline-block"
            >
              {isUrdu ? 'رابطہ کریں' : 'Contact Us'}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}

export default BusinessStrategy;

